/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  linkedSignal,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

const expandContentAnimation = trigger('expandContent', [
  state(
    'expand',
    style({
      height: '*',
      opacity: 1,
    }),
  ),
  state(
    'collapse',
    style({
      height: '0',
      opacity: 0,
    }),
  ),
  transition('expand => collapse', [animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')]),
  transition('collapse => expand', [animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')]),
]);

@Component({
  selector: 'homefield-accordion',
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss',
  animations: [expandContentAnimation],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomefieldAccordionComponent {
  /**
   * @description String representing the header.
   */
  readonly header = input<string>();

  readonly width = input('100%');
  /**
   * @description String representing the height accordions collapsed height.
   */
  readonly collapsedHeight = input('56px');
  /**
   * @description Boolean representing if accordion is inline or standalone.
   */
  readonly isInline = input(true);
  /**
   * @description Boolean representing if accordion has top border
   */
  readonly topBorder = input(true);
  /**
   * @description Boolean representing if accordion has bottom border
   */
  readonly bottomBorder = input(true);
  /**
   * @description Boolean representing if accordion is standard or dark variant.
   */
  readonly isDarkVariant = input(false);
  readonly headerClass = input('');
  /**
   * @description Boolean representing if accordion is expanded or collapsed.
   */
  readonly isExpandedInput = input(false, { alias: 'isExpanded' });
  readonly isExpanded = linkedSignal(this.isExpandedInput);
  readonly subHeader = input('');
  /**
   * @description Boolean representing if accordion header should not display once expanded.
   */
  readonly hideButton = input(false);
  readonly selectedCount = input(0);
  /**
   * @description Event emitter that outputs if accordion is expanded or collapsed.
   */
  readonly isExpandedChange = output<boolean>();
  /**
   * @description Boolean representing if accordion panel content is enabled, mainly used to remove focus of elements when panel is collapsed.
   */
  readonly isPanelContentEnabled = signal(false);

  readonly focused = output<void>();

  readonly accordionPanelIdPrefix = input<string>('');

  readonly accordionPanelIdBase = computed(
    () => `${this.accordionPanelIdPrefix()}${this.header()?.replace(/\s+/g, '-') ?? ''}`,
  );

  constructor() {
    effect(() => {
      if (this.isExpanded()) {
        this.isPanelContentEnabled.set(true);
      }
    });
  }

  /**
   * @description Used to expand or collapse accordion.
   */
  toggleAccordion() {
    if (this.isExpanded()) {
      this.isExpanded.set(false);
      this.isExpandedChange.emit(false);
      setTimeout(() => {
        // Used to make sure elements within the panel are not focusable
        if (!this.isExpanded()) {
          // make sure isExpanded hasn't changed during timeout
          this.isPanelContentEnabled.set(false);
        }
      }, 400);
    } else {
      this.isExpanded.set(true);
      this.isExpandedChange.emit(true);
      this.isPanelContentEnabled.set(true);
    }
  }

  focusAccordion() {
    this.focused.emit();
  }
}
